import axios from 'axios';
import { keyBy } from 'lodash';
import { types, flow } from 'mobx-state-tree';

import { workPlaceLevels } from 'src/constants';
import * as ResourcesAPI from 'src/services/resources';

export const SimpleResourcesModel = types
  .model('SimpleResourcesModel', {
    simpleResourceIsFetching: 0,
    countries: types.array(types.string),
    languages: types.array(types.string),
    languagesLevels: types.array(types.string),
    degrees: types.array(types.string),
    majors: types.array(types.string),
    industries: types.array(types.string),
    permissions: types.frozen<{ [key: string]: number }>({}),
    job: types.optional(
      types.model({
        functions: types.array(types.string),
        experiences: types.array(types.string),
        currencies: types.array(types.string),
        salaries: types.array(types.string),
        employmentTypes: types.array(types.string)
      }),
      {}
    ),
    workPlaces: types.array(
      types.frozen({
        id: types.number,
        name: types.string,
        level: types.string,
        parent_id: types.maybeNull(types.number),
        children: types.array(types.number)
      })
    )
  })
  .actions(self => {
    const CancelToken = axios.CancelToken;
    let source = CancelToken.source();

    return {
      abort: function() {
        source.cancel('Operation canceled by the user.');
        source = CancelToken.source();
      },
      getJobFunctions: flow(function*() {
        self.simpleResourceIsFetching = self.simpleResourceIsFetching + 1;
        try {
          const { job_functions } = yield ResourcesAPI.getJobFunctions({ cancelToken: source.token });
          self.job.functions = job_functions;
        } finally {
          self.simpleResourceIsFetching = self.simpleResourceIsFetching - 1;
        }
      }),
      getJobExperiences: flow(function*() {
        self.simpleResourceIsFetching = self.simpleResourceIsFetching + 1;
        try {
          const { experiences } = yield ResourcesAPI.getJobExperiences({ cancelToken: source.token });
          self.job.experiences = experiences;
        } finally {
          self.simpleResourceIsFetching = self.simpleResourceIsFetching - 1;
        }
      }),
      getJobCurrencies: flow(function*() {
        self.simpleResourceIsFetching = self.simpleResourceIsFetching + 1;
        try {
          const { currencies } = yield ResourcesAPI.getJobCurrencies({ cancelToken: source.token });
          self.job.currencies = currencies;
        } finally {
          self.simpleResourceIsFetching = self.simpleResourceIsFetching - 1;
        }
      }),
      getJobSalaries: flow(function*() {
        self.simpleResourceIsFetching = self.simpleResourceIsFetching + 1;
        try {
          const { salaries } = yield ResourcesAPI.getJobSalaries({ cancelToken: source.token });
          self.job.salaries = salaries;
        } finally {
          self.simpleResourceIsFetching = self.simpleResourceIsFetching - 1;
        }
      }),
      getJobEmploymentTypes: flow(function*() {
        self.simpleResourceIsFetching = self.simpleResourceIsFetching + 1;
        try {
          const { employment_types } = yield ResourcesAPI.getJobEmploymentTypes({ cancelToken: source.token });
          self.job.employmentTypes = employment_types;
        } finally {
          self.simpleResourceIsFetching = self.simpleResourceIsFetching - 1;
        }
      }),
      getDegrees: flow(function*(...args: Parameters<typeof ResourcesAPI.getDegrees>) {
        self.simpleResourceIsFetching = self.simpleResourceIsFetching + 1;
        self.degrees = yield ResourcesAPI.getDegrees({ cancelToken: source.token });
        self.simpleResourceIsFetching = self.simpleResourceIsFetching - 1;
      }),
      getMajors: flow(function*(...args: Parameters<typeof ResourcesAPI.getMajors>) {
        self.simpleResourceIsFetching = self.simpleResourceIsFetching + 1;
        self.majors = yield ResourcesAPI.getMajors({ cancelToken: source.token });
        self.simpleResourceIsFetching = self.simpleResourceIsFetching - 1;
      }),
      getIndustries: flow(function*(...args: Parameters<typeof ResourcesAPI.getIndustries>) {
        self.simpleResourceIsFetching = self.simpleResourceIsFetching + 1;
        self.industries = yield ResourcesAPI.getIndustries({ cancelToken: source.token });
        self.simpleResourceIsFetching = self.simpleResourceIsFetching - 1;
      }),
      getCountries: flow(function*(...args: Parameters<typeof ResourcesAPI.getCountries>) {
        self.simpleResourceIsFetching = self.simpleResourceIsFetching + 1;
        try {
          self.countries = yield ResourcesAPI.getCountries({ cancelToken: source.token });
        } finally {
          self.simpleResourceIsFetching = self.simpleResourceIsFetching - 1;
        }
      }),
      getLanguages: flow(function*(...args: Parameters<typeof ResourcesAPI.getLanguages>) {
        self.simpleResourceIsFetching = self.simpleResourceIsFetching + 1;
        try {
          self.languages = yield ResourcesAPI.getLanguages({ cancelToken: source.token });
        } finally {
          self.simpleResourceIsFetching = self.simpleResourceIsFetching - 1;
        }
      }),
      getLanguagesLevels: flow(function*(...args: Parameters<typeof ResourcesAPI.getLanguagesLevels>) {
        self.simpleResourceIsFetching = self.simpleResourceIsFetching + 1;
        try {
          self.languagesLevels = yield ResourcesAPI.getLanguagesLevels({ cancelToken: source.token });
        } finally {
          self.simpleResourceIsFetching = self.simpleResourceIsFetching - 1;
        }
      }),
      getWorkPlaces: flow(function*(...args: Parameters<typeof ResourcesAPI.getLanguagesLevels>) {
        self.simpleResourceIsFetching = self.simpleResourceIsFetching + 1;
        try {
          self.workPlaces = yield ResourcesAPI.getWorkPlaces({ cancelToken: source.token });
        } finally {
          self.simpleResourceIsFetching = self.simpleResourceIsFetching - 1;
        }
      }),
      getPermissions: flow(function*(...args: Parameters<typeof ResourcesAPI.getPermissions>) {
        self.permissions = yield ResourcesAPI.getPermissions({ cancelToken: source.token });
      })
    };
  })
  .views(self => ({
    get workPlacesMap() {
      return keyBy(self.workPlaces, 'id');
    },
    get unionsList() {
      return self.workPlaces.filter(item => item.level === workPlaceLevels.union);
    }
  }));
